import { z } from 'zod';

/** Lowercase, hyphen-separated (`my-post-title`) — matches the URL segment a slug becomes
 * (`/blog/:slug`, spec §4) and the exact shape the dashboard's title→slug auto-derivation
 * (spec §5) is expected to produce, whether or not the admin edits it by hand afterward. */
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const postSlugSchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .regex(SLUG_PATTERN, 'slug must be lowercase letters, numbers, and hyphens only');

/** `GET /v1/blog/posts?tag=&page=` query (spec §2). `perPage` is intentionally NOT a query
 * param — it's fixed server-side at `PUBLISHED_PAGE_SIZE` (`@repo/db`), same "not caller-
 * configurable" convention as the admin pagination schemas. */
export const publicPostsQuerySchema = z.object({
  tag: z.string().trim().min(1).max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
});
export type PublicPostsQuery = z.infer<typeof publicPostsQuerySchema>;

/** Deduplicated + lowercased (`JavaScript` and `javascript` are the same tag) — `tags` is a
 * plain `text[]` column with no join table (spec §1), so this normalization at the write
 * boundary is the ONLY thing standing between a typo'd tag and a permanently fragmented tag
 * cloud (`listTagsWithCounts` has no case-insensitive grouping of its own). Capped at 20
 * tags/post and 50 chars/tag — generous for a blog post, small enough to keep the `unnest`
 * aggregate in `listTagsWithCounts` cheap. */
const postTagsSchema = z
  .array(z.string().trim().min(1).max(50))
  .max(20)
  .transform((tags) => [...new Set(tags.map((tag) => tag.toLowerCase()))]);

/** `POST /api/admin/posts` body (spec §1/§5). `coverUrl` accepts any `https://` URL rather
 * than being restricted to `CDN_PUBLIC_URL` — the field also has to accept a manually pasted
 * external image URL, not just one that went through `POST /api/admin/media`. `publishedAt`
 * is caller-settable (rather than always server-`now()`) so a post can be backdated/scheduled
 * without a second write path — the route still ONLY sets it automatically the first time a
 * post transitions to `published` with no `publishedAt` supplied (spec §1: "set when first
 * published; drives ordering"). */
export const adminCreatePostSchema = z.object({
  slug: postSlugSchema,
  title: z.string().trim().min(1).max(200),
  excerpt: z.string().trim().min(1).max(500),
  bodyMd: z.string().min(1),
  coverUrl: z.string().url().max(2000).nullable().optional(),
  tags: postTagsSchema.default([]),
  status: z.enum(['draft', 'published']).default('draft'),
  publishedAt: z.coerce.date().nullable().optional(),
  authorId: z.string().min(1),
});
export type AdminCreatePostInput = z.infer<typeof adminCreatePostSchema>;

/**
 * `PATCH /api/admin/posts/:id` body — a REAL partial update: an omitted field must reach
 * `PostsRepo.update` as `undefined` so the repo leaves the stored value untouched.
 *
 * Why this is not `adminCreatePostSchema.partial()`: `.partial()` makes fields optional but
 * does NOT strip `.default()`, so parsing `{ coverUrl }` returned
 * `{ coverUrl, tags: [], status: 'draft' }` — and the route dutifully wrote both. Setting a
 * post's cover silently UNPUBLISHED it and erased its tags (hit in production 2026-08-07 on
 * the first live post). The create schema's defaults are correct for create and wrong for
 * update, so update declares its own fields with no defaults at all.
 */
export const adminUpdatePostSchema = z.object({
  slug: postSlugSchema.optional(),
  title: z.string().trim().min(1).max(200).optional(),
  excerpt: z.string().trim().min(1).max(500).optional(),
  bodyMd: z.string().min(1).optional(),
  coverUrl: z.string().url().max(2000).nullable().optional(),
  tags: postTagsSchema.optional(),
  status: z.enum(['draft', 'published']).optional(),
  publishedAt: z.coerce.date().nullable().optional(),
  authorId: z.string().min(1).optional(),
});
export type AdminUpdatePostInput = z.infer<typeof adminUpdatePostSchema>;

/** `POST /api/admin/authors` body (spec §1: real people, AI-assisted but never fabricated —
 * enforced editorially, not by this schema). `persona` is blog-polish spec §F: a briefing
 * note describing this author's writing voice, fed to the post editor's "Copy AI prompt"
 * button (spec §G) — admin-only, never rendered publicly (see `Author.persona`'s schema
 * comment and `AuthorSummaryDto`, which deliberately omits it). */
export const adminCreateAuthorSchema = z.object({
  slug: postSlugSchema,
  name: z.string().trim().min(1).max(200),
  role: z.string().trim().max(200).nullable().optional(),
  bio: z.string().trim().max(2000).nullable().optional(),
  avatarUrl: z.string().url().max(2000).nullable().optional(),
  persona: z.string().trim().max(1200).nullable().optional(),
});
export type AdminCreateAuthorInput = z.infer<typeof adminCreateAuthorSchema>;

/** `PATCH /api/admin/authors/:id` body — every field optional, same partial-update
 * convention as `adminUpdatePostSchema`. */
export const adminUpdateAuthorSchema = adminCreateAuthorSchema.partial();
export type AdminUpdateAuthorInput = z.infer<typeof adminUpdateAuthorSchema>;

/** `POST /api/admin/posts/preview` — stateless Markdown render for the editor's split view.
 * Deliberately NOT tied to a post id: previewing must never write, or editing a published
 * post would leak half-finished paragraphs to readers (see the route's own comment). */
export const adminPreviewMarkdownSchema = z.object({
  bodyMd: z.string().max(200_000),
});
export type AdminPreviewMarkdownInput = z.infer<typeof adminPreviewMarkdownSchema>;
